export const VOICE_ID = "no_NO-talesyntese-medium";
export const ESTIMATED_MODEL_BYTES = 63000000;
export const VOICE_PREF_STORAGE_KEY = "scrollytelling_tts_voice_pref";
export const MODEL_CACHED_STORAGE_KEY = "scrollytelling_piper_no_cached";
export const VITS_WEB_CDN_URL =
  "https://cdn.jsdelivr.net/npm/@diffusionstudio/vits-web@1.0.3/+esm";

const LEGACY_PREF_STORAGE_KEY = "kit_piper_tts_preference";

const resolveStorage = (storage) => {
  if (storage) return storage;
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
};

export const getVoicePreference = (storage) => {
  const s = resolveStorage(storage);
  if (!s) return null;
  const pref = s.getItem(VOICE_PREF_STORAGE_KEY) || s.getItem(LEGACY_PREF_STORAGE_KEY);
  if (pref === "neural" || pref === "system") {
    return pref;
  }
  return null;
};

export const setVoicePreference = (mode, storage) => {
  if (mode !== "neural" && mode !== "system") {
    throw new Error("Ugyldig talepreferanse: må være 'neural' eller 'system'");
  }
  const s = resolveStorage(storage);
  if (s) {
    s.setItem(VOICE_PREF_STORAGE_KEY, mode);
    s.setItem(LEGACY_PREF_STORAGE_KEY, mode);
  }
};

export const isModelCached = async ({ storage, vitsModule = null } = {}) => {
  const s = resolveStorage(storage);
  if (s?.getItem(MODEL_CACHED_STORAGE_KEY) === "true") {
    return true;
  }
  if (vitsModule && typeof vitsModule.stored === "function") {
    try {
      const storedVoices = await vitsModule.stored();
      if (Array.isArray(storedVoices) && storedVoices.includes(VOICE_ID)) {
        s?.setItem(MODEL_CACHED_STORAGE_KEY, "true");
        return true;
      }
    } catch {
      return false;
    }
  }
  return false;
};

export const markModelCached = (cached = true, storage) => {
  const s = resolveStorage(storage);
  if (!s) return;
  if (cached) {
    s.setItem(MODEL_CACHED_STORAGE_KEY, "true");
  } else {
    s.removeItem(MODEL_CACHED_STORAGE_KEY);
  }
};

export const formatDownloadProgress = ({
  loadedBytes = 0,
  totalBytes = ESTIMATED_MODEL_BYTES,
} = {}) => {
  const effectiveTotal =
    typeof totalBytes === "number" && totalBytes > 0
      ? totalBytes
      : ESTIMATED_MODEL_BYTES;
  const clampedLoaded = Math.max(
    0,
    Math.min(Number(loadedBytes) || 0, effectiveTotal)
  );
  const percent = Math.min(
    100,
    Math.max(0, Math.round((clampedLoaded / effectiveTotal) * 100))
  );
  const loadedMb = (clampedLoaded / (1024 * 1024)).toFixed(1);
  const totalMb = (effectiveTotal / (1024 * 1024)).toFixed(1);
  const label = `Laster ned norsk stemmemodell: ${percent} % (${loadedMb} / ${totalMb} MB)`;

  return {
    loadedBytes: clampedLoaded,
    totalBytes: effectiveTotal,
    percent,
    percentage: percent,
    loadedMb,
    totalMb,
    label,
  };
};

export const defaultVitsLoader = () =>
  import(/* webpackIgnore: true */ VITS_WEB_CDN_URL);

export const downloadNorwegianModel = async ({
  onProgress = () => {},
  signal = null,
  vitsLoader = defaultVitsLoader,
  storage,
} = {}) => {
  if (signal?.aborted) {
    throw new Error("Download aborted");
  }

  const vits = await vitsLoader();

  await vits.download(VOICE_ID, (progress) => {
    if (signal?.aborted) {
      throw new Error("Download aborted");
    }
    const formatted = formatDownloadProgress({
      loadedBytes: progress?.loaded || 0,
      totalBytes: progress?.total || ESTIMATED_MODEL_BYTES,
    });
    onProgress(formatted);
  });

  if (signal?.aborted) {
    throw new Error("Download aborted");
  }

  markModelCached(true, storage);
  return true;
};

const CENTURY_MAP = {
  "1000": "tusen",
  "1100": "ellevehundre",
  "1200": "tolvhundre",
  "1300": "trettenhundre",
  "1400": "fjortenhundre",
  "1500": "femtenhundre",
  "1600": "sekstenhundre",
  "1700": "syttenhundre",
  "1800": "attenhundre",
  "1900": "nittenhundre",
  "2000": "totusen",
};

const CENTURY_PREFIXES = {
  11: "elleve",
  12: "tolv",
  13: "tretten",
  14: "fjorten",
  15: "femten",
  16: "seksten",
  17: "sytten",
  18: "atten",
  19: "nitten",
  20: "tjue",
};

const ONES = [
  "", "en", "to", "tre", "fire", "fem", "seks", "sju", "åtte", "ni",
];

const TEENS = [
  "ti", "elleve", "tolv", "tretten", "fjorten", "femten", "seksten", "sytten", "atten", "nitten",
];

const TENS = [
  "", "", "tjue", "tretti", "førti", "femti", "seksti", "sytti", "åtti", "nitti",
];

function twoDigitsToWords(n) {
  if (n < 10) return ONES[n];
  if (n < 20) return TEENS[n - 10];
  const ten = Math.floor(n / 10);
  const one = n % 10;
  return `${TENS[ten]}${ONES[one]}`;
}

export function convertYearToWords(yearStr) {
  const num = parseInt(yearStr, 10);
  if (isNaN(num) || num < 1100 || num > 2099) {
    return yearStr;
  }
  if (num === 2000) {
    return "to tusen";
  }
  if (num > 2000 && num < 2010) {
    return `to tusen og ${ONES[num % 10]}`;
  }
  if (num >= 2010 && num <= 2099) {
    const lastTwo = num % 100;
    return `tjue ${twoDigitsToWords(lastTwo)}`;
  }
  const cent = Math.floor(num / 100);
  const rest = num % 100;
  const prefix = CENTURY_PREFIXES[cent];
  if (rest === 0) {
    return `${prefix} hundre`;
  }
  if (rest < 10) {
    return `${prefix} null ${ONES[rest]}`;
  }
  return `${prefix} ${twoDigitsToWords(rest)}`;
}

export const normalizeNorwegianTtsText = (text) => {
  if (!text || typeof text !== "string") {
    return "";
  }

  let result = text;

  // 1. Names and loanwords with hard K before i and y (prevents espeak-ng soft kj-sound)
  result = result
    .replace(/\bKim(s)?\b/g, "Khim$1")
    .replace(/\bkim(s)?\b/g, "khim$1")
    .replace(/\bKitty(s)?\b/g, "Khitty$1")
    .replace(/\bkitty(s)?\b/g, "khitty$1")
    .replace(/\bkick\b/g, "khick")
    .replace(/\bkid(s)?\b/g, "khid$1");

  // 2. Year ranges (convert before individual 4-digit years)
  result = result.replace(
    /(\b(?:1[1-9]\d\d|20\d\d))\s*[–—-]\s*(\b(?:1[1-9]\d\d|20\d\d)\b)/g,
    "$1 til $2"
  );

  // 3. Centuries (1000-tallet .. 2000-tallet, case-insensitive, including -talet)
  result = result.replace(
    /\b(1000|1100|1200|1300|1400|1500|1600|1700|1800|1900|2000)-(tal{1,2}et)\b/gi,
    (match, year, suffix) => {
      const base = CENTURY_MAP[year];
      const suf = suffix.toLowerCase();
      return `${base}${suf}`;
    }
  );

  // 4. Standalone 4-digit years (1100-2099, excluding quantities followed by measurement/currency units)
  result = result.replace(
    /\b(1[1-9]\d\d|20\d\d)\b(?!\s*(?:meter|m\.o\.h\.|moh|m\b|km|kr|kg|prosent|%|kroner|stk))/gi,
    (match) => convertYearToWords(match)
  );

  // 5. Common Norwegian abbreviations
  result = result
    .replace(/\bf\.?\s*eks\.(?!\w)/gi, "for eksempel")
    .replace(/\bca\.(?!\w)/gi, "sirka")
    .replace(/\bbl\.?\s*a\.(?!\w)/gi, "blant annet")
    .replace(/\bosv\.(\s*($|[A-ZÆØÅ]))/g, "og så videre.$1")
    .replace(/\bosv\.(?!\w)/gi, "og så videre")
    .replace(/\bm\.?\s*m\.(\s*($|[A-ZÆØÅ]))/g, "med mer.$1")
    .replace(/\bm\.?\s*m\.(?!\w)/gi, "med mer")
    .replace(/\bdvs\.(?!\w)/gi, "det vil si")
    .replace(/\bp\.?\s*g\.?\s*a\.(?!\w)/gi, "på grunn av")
    .replace(/\bnr\.(?!\w)/gi, "nummer")
    .replace(/\bkl\.(?!\w)/gi, "klokka")
    .replace(/\bf\.?\s*Kr\.(\s*($|[A-ZÆØÅ]))/g, "før Kristus.$1")
    .replace(/\bf\.?\s*Kr\.(?!\w)/g, "før Kristus")
    .replace(/\be\.?\s*Kr\.(\s*($|[A-ZÆØÅ]))/g, "etter Kristus.$1")
    .replace(/\be\.?\s*Kr\.(?!\w)/g, "etter Kristus")
    .replace(/\b(mfl\.|m\.?\s*fl\.)(\s*($|[A-ZÆØÅ]))/gi, "med flere.$2")
    .replace(/\b(mfl\.?|m\.?\s*fl\.)(?!\w)/gi, "med flere")
    .replace(/\b(m\.o\.h\.|moh\.)(\s*($|[A-ZÆØÅ]))/gi, "meter over havet.$2")
    .replace(/\b(m\.o\.h\.|moh\.?)(?!\w)/gi, "meter over havet");

  // 6. Standalone dashes to pauses (commas)
  result = result.replace(/\s+[–—]\s+/g, ", ");

  // 7. Symbols
  result = result
    .replace(/%/g, " prosent")
    .replace(/&/g, " og ");

  result = result.replace(/[ \t]+/g, " ");

  return result;
};

export const sanitizeTtsText = (text) => {
  if (!text || typeof text !== "string") {
    return "";
  }
  const cleanHtml = text.replace(/<[^>]*>/g, " ");
  const normalized = normalizeNorwegianTtsText(cleanHtml);
  return normalized.replace(/\s+/g, " ").trim();
};

export const synthesizeNorwegianSpeech = async (
  text,
  {
    vitsLoader = defaultVitsLoader,
    urlCreator = typeof URL !== "undefined" && URL.createObjectURL
      ? URL.createObjectURL.bind(URL)
      : null,
    onProgress = null,
  } = {}
) => {
  const cleanText = sanitizeTtsText(text);
  if (!cleanText) {
    throw new Error("Syntesetekst kan ikke være tom");
  }

  const vits = await vitsLoader();
  const wavBlob = await (onProgress
    ? vits.predict({ text: cleanText, voiceId: VOICE_ID }, onProgress)
    : vits.predict({ text: cleanText, voiceId: VOICE_ID }));

  const objectUrl = urlCreator ? urlCreator(wavBlob) : null;

  return {
    wavBlob,
    objectUrl,
  };
};
